import { useMemo, useState } from 'react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Container } from '../components/Container';
import { Section } from '../components/Section';

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Home() {
  return (
    <main>
      <Hero />

      <Section
        id="about"
        eyebrow="About"
        title="Practical communication support for modern teams"
        description="We help businesses manage customer communication, follow-ups, and outreach processes to improve engagement and build long-term relationships."
      />

      <Section
        id="services"
        eyebrow="Services"
        title="Focused services that strengthen client relationships"
        description="Simple, professional support designed to keep your conversations clear, timely, and consistent."
        className="bg-white"
      >
        <div className="grid gap-6 md:grid-cols-3">
          <Card title="Client Communication Management">
            Organized handling of client conversations so nothing important is
            missed and expectations stay clear.
          </Card>
          <Card title="Email Follow-ups & Coordination">
            Timely follow-ups and scheduling coordination to support smooth,
            reliable communication.
          </Card>
          <Card title="Customer Engagement Solutions">
            Structured engagement touchpoints that help maintain relationships
            and improve customer experience.
          </Card>
        </div>
      </Section>

      <Section
        id="why"
        eyebrow="Why Choose Us"
        title="Professional, reliable, and built for your workflow"
        description="We prioritize clarity and consistency so your business can communicate with confidence."
      >
        <div className="grid gap-6 md:grid-cols-2">
          <Point title="Reliable communication systems">
            Repeatable processes and organized tracking to help keep conversations
            on schedule.
          </Point>
          <Point title="Professional client handling">
            A respectful, service-first approach that reflects well on your brand.
          </Point>
          <Point title="Scalable solutions">
            Support that adapts as your team grows, from routine updates to busy
            periods.
          </Point>
          <Point title="Business-focused approach">
            Clear priorities, straightforward coordination, and results that
            support long-term customer relationships.
          </Point>
        </div>
      </Section>

      <Contact />
    </main>
  );
}

function Hero() {
  return (
    <section aria-label="Hero" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-slate-50" />
      <div className="absolute -top-32 left-1/2 h-72 w-[52rem] -translate-x-1/2 rounded-full bg-sky-200/40 blur-3xl" />

      <Container className="relative py-16 sm:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
              Business Communication Solutions
            </p>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
              Grow With Call
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-700 sm:text-lg">
              Helping businesses connect, communicate, and grow through effective
              client engagement.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button onClick={() => scrollToId('contact')}>Contact Us</Button>
              <Button
                variant="secondary"
                onClick={() => scrollToId('services')}
              >
                View Services
              </Button>
            </div>

            <dl className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
              <Stat label="Clear follow-ups" value="Consistent" />
              <Stat label="Client handling" value="Professional" />
              <Stat label="Support model" value="Scalable" />
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">
                What you can expect
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-700">
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 flex-none rounded-full bg-sky-700" />
                  Clear communication and timely updates
                </li>
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 flex-none rounded-full bg-sky-700" />
                  Organized follow-up coordination
                </li>
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 flex-none rounded-full bg-sky-700" />
                  A service approach that supports trust
                </li>
              </ul>

              <div className="mt-6 rounded-2xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200">
                <p className="text-xs font-medium text-slate-600">Email</p>
                <a
                  className="mt-1 block text-sm font-semibold text-sky-800 hover:text-sky-900"
                  href="mailto:founder@growwithcall.com"
                >
                  founder@growwithcall.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <dt className="text-xs font-medium text-slate-600">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function Point({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-slate-700">{children}</p>
    </div>
  );
}

function Contact() {
  const [status, setStatus] = useState('idle'); // idle | sent

  const fields = useMemo(
    () => [
      { name: 'name', label: 'Name', type: 'text', autoComplete: 'name' },
      { name: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
    ],
    []
  );

  return (
    <section id="contact" aria-label="Contact" className="bg-white py-14 sm:py-16">
      <Container>
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-sm font-medium tracking-wide text-slate-600">
              Contact
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Reach out to Grow With Call
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-700">
              For service inquiries and general questions, email us or use the
              contact form. We’ll respond as soon as possible.
            </p>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-xs font-medium text-slate-600">Email</p>
              <a
                className="mt-1 block text-sm font-semibold text-sky-800 hover:text-sky-900"
                href="mailto:founder@growwithcall.com"
              >
                founder@growwithcall.com
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm sm:p-8"
              onSubmit={(e) => {
                e.preventDefault();
                setStatus('sent');
              }}
            >
              <div className="grid gap-5 sm:grid-cols-2">
                {fields.map((f) => (
                  <div key={f.name} className="sm:col-span-1">
                    <label
                      className="block text-sm font-medium text-slate-700"
                      htmlFor={f.name}
                    >
                      {f.label}
                    </label>
                    <input
                      id={f.name}
                      name={f.name}
                      type={f.type}
                      required
                      autoComplete={f.autoComplete}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-sky-500 focus:ring-2"
                    />
                  </div>
                ))}

                <div className="sm:col-span-2">
                  <label
                    className="block text-sm font-medium text-slate-700"
                    htmlFor="message"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="mt-2 w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-sky-500 focus:ring-2"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs leading-5 text-slate-600">
                  This form is for inquiries only and does not create a contract
                  or service agreement.
                </p>
                <Button type="submit">Send message</Button>
              </div>

              {status === 'sent' && (
                <p
                  className="mt-4 rounded-xl bg-white p-3 text-sm font-medium text-slate-800 ring-1 ring-inset ring-slate-200"
                  role="status"
                >
                  Thanks — your message is ready to send. Since there’s no backend
                  connected, please email us directly at{' '}
                  <a
                    className="font-semibold text-sky-800 hover:text-sky-900"
                    href="mailto:founder@growwithcall.com"
                  >
                    founder@growwithcall.com
                  </a>
                  .
                </p>
              )}
            </form>
          </div>
        </div>
      </Container>
    </section>
  );
}

